import { useEffect, useMemo } from "react";
import { Form } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateShipmentSchedule } from "api";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { useGetShipmentDetails } from "api/queries/shipments/useGetShipmentDetails";
import { useGetShipmentSchedule } from "api/queries/shipments/useGetShipmentSchedule";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import { cleanFormValuesForPayload, prefillFormFromSchedule, validateMultistopSchedule } from "helpers/editScheduleHelpers";
import errorToast from "helpers/errorToast";
import ShipmentTypes from "constants/ShipmentTypes";
import { IEditScheduleForm, IEditSchedulePayload } from "./types";
import EditScheduleRequestSummary from "./components/EditScheduleRequestSummary";
import EditScheduleForm from "./components/EditScheduleForm";
import EditScheduleFormSidebar from "./components/EditScheduleFormSidebar";
import EditFlightForm from "./components/EditFlightForm";
import EditOceanForm from "./components/EditOceanForm";
import EditScheduleMultistopStopsForm from "./MultiStop/EditScheduleMultistopStopsForm";
import EditScheduleMultistopVesselForm from "./MultiStop/EditScheduleMultistopVesselForm";
import EditScheduleMultistopSidebar from "./MultiStop/EditScheduleMultistopSidebar";
import "./EditSchedule.scss";

interface Props {
  isOpen: boolean;
  close: () => void;
}

export default function EditScheduleDialog({ isOpen, close }: Props) {
  const id = useParams().id?.toUpperCase()!;
  const queryClient = useQueryClient();

  const methods = useForm<IEditScheduleForm>({
    defaultValues: {
      pickup_date: "",
      pickup_time: "",
      estimated_delivery_date: "",
      estimated_delivery_time: "",
      stops: [],
      vessels: [],
      flights: [],
    },
  });
  const { handleSubmit, reset } = methods;

  const { data: shipmentDetailsData, refetch: updateSchedule } = useGetShipmentDetails(id, true, {
    enabled: !!id && isOpen,
    refetchOnMount: false,
  });

  const { data: shipmentScheduleData, isLoading: isScheduleLoading, refetch } = useGetShipmentSchedule(id, {
    enabled: !!id && isOpen,
    refetchOnMount: false,
  });

  const isPoPSubmitted = shipmentDetailsData?.schedule?.is_proof_of_pickup_submitted;

  const isMultiStop = useMemo(() => {
    if (shipmentDetailsData?.shipment?.shipment_type != null) {
      return shipmentDetailsData.shipment.shipment_type === ShipmentTypes.MULTISTOP;
    }
    return ((shipmentScheduleData as { stops?: unknown[] } | undefined)?.stops?.length ?? 0) > 0;
  }, [shipmentDetailsData, shipmentScheduleData]);

  useEffect(() => {
    if (!isOpen || !shipmentScheduleData) return;
    reset(prefillFormFromSchedule(shipmentScheduleData, { isMultiStop }));
  }, [isOpen, shipmentScheduleData, isMultiStop, reset]);

  const { mutate, isLoading: isSubmitting } = useMutation({
    mutationFn: (newSchedule: IEditSchedulePayload) => updateShipmentSchedule(id, newSchedule),
    onSuccess: () => {
      toast("Schedule successfully updated", { type: "success" });
      refetch();
      updateSchedule();
      queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentGeoDataDetails(id) });
      queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentSchedule(id) });
      queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentDetails(id) });
      close();
      reset();
    },
    onError: (err) => {
      errorToast(err ?? "Fail to update schedule");
    },
  });

  const isFlight = shipmentDetailsData?.service?.freight_mode_type === "air";
  const isOcean = shipmentDetailsData?.service?.freight_mode_type === "ocean";

  const handleSubmitEditSchedule = (values: IEditScheduleForm) => {
    if (isMultiStop) {
      const multistopError = validateMultistopSchedule(values);
      if (multistopError) {
        toast.error(multistopError);
        return;
      }
    }
    const payload = cleanFormValuesForPayload(values, {
      isFlight,
      isOcean,
      isPoPSubmitted: isMultiStop ? false : isPoPSubmitted,
      isMultiStop,
    });
    mutate(payload);
  };

  const onClose = () => {
    close();
    reset();
  };

  return (
    <Dialog
      className="edit-schedule-dialog"
      title={<FormattedMessage id="components.molecules.navigation.editSchedule" defaultMessage="Edit schedule" />}
      show={isOpen}
      onHide={onClose}
    >
      <h6>
        <FormattedMessage id="tracking.components.editScheduleDialog.requestSummary" defaultMessage="Request summary" />
      </h6>
      <div className="mt-3">
        <EditScheduleRequestSummary />
      </div>

      <hr />

      <FormProvider {...methods}>
        <Form className="w-100" onSubmit={handleSubmit(handleSubmitEditSchedule)}>
          <div className="position-relative">
            {isScheduleLoading && (
              <div
                className="edit-schedule-dialog-loading-overlay d-flex justify-content-center align-items-center"
                style={{ position: "absolute", inset: 0, background: "rgba(255, 255, 255, 0.9)", zIndex: 2 }}
              >
                <div className="spinner-border text-primary" role="status" aria-label="Loading schedule">
                  <span className="sr-only">Loading</span>
                </div>
              </div>
            )}
            {isMultiStop ? (
              <>
                <EditScheduleMultistopSidebar />
                <div className="mt-3">
                  <EditScheduleMultistopStopsForm />
                </div>

                <hr />

                <h6>
                  {isFlight && (
                    <FormattedMessage id="tracking.components.editScheduleDialog.editFlight" defaultMessage="Edit flight" />
                  )}
                  {isOcean && (
                    <FormattedMessage
                      id="tracking.components.editScheduleDialog.vesselInformation"
                      defaultMessage="Vessel Information"
                    />
                  )}
                </h6>
                <div className="edit-schedule-form-container mt-3">
                  {isFlight && <EditFlightForm />}
                  {isOcean && <EditScheduleMultistopVesselForm />}
                </div>
              </>
            ) : (
              <>
                <EditScheduleFormSidebar />
                <div className="mt-3">
                  <EditScheduleForm shipmentDetails={shipmentDetailsData} />
                </div>

                <hr />

                <h6>
                  {isFlight && (
                    <FormattedMessage id="tracking.components.editScheduleDialog.editFlight" defaultMessage="Edit flight" />
                  )}
                  {isOcean && (
                    <FormattedMessage
                      id="tracking.components.editScheduleDialog.vesselInformation"
                      defaultMessage="Vessel Information"
                    />
                  )}
                </h6>
                <div className="edit-schedule-form-container mt-3">
                  {isFlight && <EditFlightForm />}
                  {isOcean && <EditOceanForm />}
                </div>
              </>
            )}

            <div className="d-flex justify-content-between mt-5">
              <Button size="lg" variant="outline-primary" onClick={close} disabled={isScheduleLoading}>
                <FormattedMessage id="general.discard" defaultMessage="Discard" />
              </Button>

              <Button size="lg" variant="primary" type="submit" disabled={isSubmitting || isScheduleLoading}>
                <FormattedMessage id="general.save" defaultMessage="Save" />
              </Button>
            </div>
          </div>
        </Form>
      </FormProvider>
    </Dialog>
  );
}
